<script setup lang="ts">
import { ref, computed, watch, onMounted, useAttrs } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useDismissablePopover } from '@/composables/useDismissablePopover'
import { listEditions } from '@/api/events'
import type { Edition } from '@/types/events'

const props = defineProps<{
  currentEventSlug: string
  currentEditionSlug: string
}>()

const emit = defineEmits<{
  (e: 'navigate'): void
  (e: 'select', edition: Edition): void
}>()

const attrs = useAttrs()

const { t } = useI18n()
const router = useRouter()
const route = useRoute()

const rootRef = ref<HTMLElement | null>(null)

// Closes on an outside click only (no Escape or focus handling), as the sidebar switchers always did.
const switcher = useDismissablePopover({
  idPrefix: 'edition-switcher',
  boundaryRef: rootRef,
  singleOpen: true,
  closeOnOutside: ['click'],
})
const open = switcher.isOpen
const editions = ref<Edition[]>([])
const loading = ref(false)

const currentEdition = computed(() =>
  editions.value.find((e) => e.slug === props.currentEditionSlug) ?? null,
)

watch(() => props.currentEventSlug, () => {
  editions.value = []
  void loadEditions()
})

async function loadEditions(): Promise<void> {
  if (editions.value.length > 0) return
  loading.value = true
  try {
    editions.value = await listEditions(props.currentEventSlug)
  } finally {
    loading.value = false
  }
}

function toggleOpen(): void {
  switcher.toggle()
  if (open.value) void loadEditions()
}

async function selectEdition(edition: Edition): Promise<void> {
  switcher.close()
  emit('navigate')
  emit('select', edition)
  if (!('onSelect' in attrs)) {
    const newPath = route.path.replace(
      `/organizer/events/${props.currentEventSlug}/editions/${props.currentEditionSlug}`,
      `/organizer/events/${props.currentEventSlug}/editions/${edition.slug}`,
    )
    await router.push(newPath)
  }
}

onMounted(() => {
  void loadEditions()
})

</script>

<template>
  <div ref="rootRef" data-edition-switcher class="relative px-3 py-3 border-b border-slate-800">
    <button
      type="button"
      class="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800/50 hover:bg-slate-800 transition-colors text-left"
      @click="toggleOpen"
    >
      <div class="flex-1 min-w-0">
        <p class="text-xs text-slate-500 font-medium uppercase tracking-wider">
          {{ t('nav.sidebar.edition') }}
        </p>
        <p class="text-sm text-white font-semibold truncate mt-0.5">
          {{ currentEdition?.name ?? '...' }}
        </p>
      </div>
      <svg
        class="w-4 h-4 text-slate-400 shrink-0 transition-transform"
        :class="open ? 'rotate-180' : ''"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>
    </button>

    <div
      v-if="open"
      class="absolute left-3 right-3 top-full mt-1 z-10 bg-slate-800 border border-slate-700 rounded-lg shadow-xl py-1 max-h-64 overflow-y-auto"
    >
      <div v-if="loading" class="px-4 py-3 text-sm text-slate-400">
        {{ t('common.loading') }}
      </div>
      <button
        v-for="ed in editions"
        :key="ed.id"
        type="button"
        class="w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center gap-2"
        :class="ed.slug === currentEditionSlug
          ? 'text-white font-semibold bg-slate-700'
          : 'text-slate-300 hover:text-white hover:bg-slate-700/50'"
        @click="selectEdition(ed)"
      >
        <span class="flex-1 truncate">{{ ed.name }}</span>
        <svg
          v-if="ed.slug === currentEditionSlug"
          class="w-4 h-4 text-emerald-400 shrink-0"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
        </svg>
      </button>
    </div>
  </div>
</template>
